/**
 * ProductionGanttMovement.jsx
 *
 * Order Movement Timeline Gantt Chart
 *
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  memo,
} from "react";
import axios from "axios";
import { appendProductionInstanceScope } from '../productionRequestScope';
import {
  Button,
  ButtonGroup,
  Badge,
  Card,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { StyledEngineProvider } from '@mui/material/styles';


import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import TimelineIcon from "@mui/icons-material/Timeline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule"; 
import DateRangeIcon from "@mui/icons-material/DateRange"; 
import EventIcon from "@mui/icons-material/Event"; 

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"; 

import JobCardModal from "./JobCardModal";
import ProductionFrappeGantt from "./ProductionFrappeGantt";
import { toast, Toaster } from "react-hot-toast";
const scopedProductionUrl = (input) => appendProductionInstanceScope(input, {
  pathname: window.location.pathname, apiBase: process.env.REACT_APP_NODEJS_SERVER_URL,
});

// Theme Context
const ThemeContext = createContext();

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("gantt-movement-theme");
    return saved === "dark";
  });

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      const newValue = !prev;
      localStorage.setItem("gantt-movement-theme", newValue ? "dark" : "light");
      return newValue;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Colors

const THEMES = {
  light: {
    background: "#FFFFFF",
    surface: "#F9FAFB",
    surfaceHover: "#F3F4F6",
    border: "#E5E7EB",
    borderLight: "#F3F4F6",
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    textMuted: "#9CA3AF",
    cardBg: "#FFFFFF",
    success: "#10B981",
    warning: "#F59E0B",
    danger: "#EF4444",
    info: "#3B82F6",
    purple: "#8B5CF6",
    shadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
    shadowMd: "0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)",
    shadowLg: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
  },
  dark: {
    background: "#0F172A",
    surface: "#1E293B",
    surfaceHover: "#334155",
    border: "#334155",
    borderLight: "#475569",
    textPrimary: "#F1F5F9",
    textSecondary: "#CBD5E1",
    textMuted: "#94A3B8",
    cardBg: "#1E293B",
    success: "#10B981",
    warning: "#F59E0B",
    danger: "#EF4444",
    info: "#3B82F6",
    purple: "#8B5CF6",
    shadow: "0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
    shadowMd: "0 4px 6px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3)",
    shadowLg: "0 10px 15px rgba(0,0,0,0.5), 0 4px 6px rgba(0,0,0,0.4)",
  },
};

// Kanban Colors
const EVENT_COLORS = {
  Planned: {
    bar: "#64748B",
    progress: "#475569",
    bg: "#F1F5F9",
    text: "#475569",
    label: "Planned",
  },
  Scheduled: {
    bar: "#3B82F6",
    progress: "#2563EB",
    bg: "#DBEAFE",
    text: "#1E40AF",
    label: "Scheduled",
  },
  In_Production: {
    bar: "#F59E0B",
    progress: "#D97706",
    bg: "#FEF3C7",
    text: "#B45309",
    label: "In Production",
  },
  InProduction: {
    bar: "#F59E0B",
    progress: "#D97706",
    bg: "#FEF3C7",
    text: "#B45309",
    label: "In Production",
  },
  QC: {
    bar: "#EF4444",
    progress: "#DC2626",
    bg: "#FEE2E2",
    text: "#991B1B",
    label: "Quality Check",
  },
  Completed: {
    bar: "#10B981",
    progress: "#059669",
    bg: "#D1FAE5",
    text: "#065F46",
    label: "Completed",
  },
  On_Hold: {
    bar: "#8B5CF6",
    progress: "#7C3AED",
    bg: "#EDE9FE",
    text: "#5B21B6",
    label: "On Hold",
  },
  OnHold: {
    bar: "#8B5CF6",
    progress: "#7C3AED",
    bg: "#EDE9FE",
    text: "#5B21B6",
    label: "On Hold",
  },
  SCHEDULE: {
    bar: "#3B82F6",
    progress: "#2563EB",
    bg: "#DBEAFE",
    text: "#1E40AF",
    label: "Scheduled",
  },
  START: {
    bar: "#F59E0B",
    progress: "#D97706",
    bg: "#FEF3C7",
    text: "#B45309",
    label: "Started",
  },
  PAUSE: {
    bar: "#8B5CF6",
    progress: "#7C3AED",
    bg: "#EDE9FE",
    text: "#5B21B6",
    label: "Paused",
  },
  RESUME: {
    bar: "#F59E0B",
    progress: "#D97706",
    bg: "#FEF3C7",
    text: "#B45309",
    label: "Resumed",
  },
  COMPLETE: {
    bar: "#10B981",
    progress: "#059669",
    bg: "#D1FAE5",
    text: "#065F46",
    label: "Completed",
  },
};


const VIEW_MODES = [
  { mode: "Day", label: "Day", icon: CalendarTodayIcon },
  { mode: "Week", label: "Week", icon: DateRangeIcon },
  { mode: "Month", label: "Month", icon: EventIcon },
  { mode: "Year", label: "Year", icon: CalendarMonthIcon },















];

// Avatar Helpers
const getAvatarColor = (name) => {
  const colors = [
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#EC4899",
    "#06B6D4",
    "#84CC16",
  ];
  const charCode = name?.charAt(0).toUpperCase().charCodeAt(0) || 65;
  return colors[charCode % colors.length];
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

// UTILITY FUNCTIONS

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

const formatDateTime = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateShort = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const formatDuration = (start, end) => {
  const diff = new Date(end) - new Date(start);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const normalizeStatus = (status) => {
  if (!status) return "Planned";
  return status.replace(/_/g, "");
};

// Parse operator from note

const parseOperatorFromNote = (note) => {
  if (!note) return null;

  const operatorMatch = note.match(/operator=([^;]+)/);
  if (operatorMatch && operatorMatch[1]) {
    return operatorMatch[1].trim();
  }

  return null;
};

// Audit transformation

const transformAuditToGanttTasks = (auditData, parentTask, costData) => {
  const { events, order_no, external_key, pwo_id } = auditData;

  if (!events || events.length === 0) return [];

  const tasks = [];
  const currentTime = new Date();

  const firstEvent = events[0];
  const lastEvent = events[events.length - 1];
  const lastEventStatus = normalizeStatus(lastEvent.to || lastEvent.lane);

  const parentStart = new Date(firstEvent.timestamp);
  const parentEnd =
    lastEventStatus === "Completed"
      ? new Date(lastEvent.timestamp)
      : parentTask?.end || currentTime;

  const statusColor = EVENT_COLORS[lastEventStatus] || EVENT_COLORS["Planned"];

  // const operator = lastEvent.operator || lastEvent.machineName || parentTask?.cardData?.machine || 'Unassigned';

  const scheduleEvent = events.find((e) => e.type === "SCHEDULE");
  const operatorFromNote = scheduleEvent
    ? parseOperatorFromNote(scheduleEvent.note)
    : null;
  const operator =
    operatorFromNote ||
    lastEvent.machineName ||
    parentTask?.cardData?.machine ||
    "Unassigned";

  //  Parent task - Default Collapsed
  const parentTaskData = {
    id: `order-${pwo_id}`,
    name: `#${order_no} - ${parentTask?.cardData?.customer || "Order"}`,
    type: "project",
    start: parentStart,
    end: parentEnd,
    progress: calculateOrderProgress(events),
    hideChildren: true, // Default Collapsed
    isDisabled: false,
    styles: {
      backgroundColor: statusColor.bar,
      // progressColor: statusColor.progress,
      // progressSelectedColor: statusColor.progress,
      progressColor: statusColor.bar,
      progressSelectedColor: statusColor.bar,
      backgroundSelectedColor: statusColor.bar,
    },
    cardData: parentTask?.cardData,
    cost: costData,
    operator: operator,
    metadata: {
      isParent: true,
      orderNumber: order_no,
      pwoId: pwo_id,
      externalKey: external_key,
      totalEvents: events.length,
      currentStatus: lastEventStatus,
      operator: operator,
      statusColor: statusColor,
    },
  };

  tasks.push(parentTaskData);

  // Child tasks
  events.forEach((event, index) => {
    const nextEvent = events[index + 1];
    const eventStart = new Date(event.timestamp);
    let eventEnd = nextEvent
      ? new Date(nextEvent.timestamp)
      : normalizeStatus(event.to) === "Completed"
        ? eventStart
        : currentTime;

    if (eventEnd <= eventStart) {
      eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
    }

    const childStatusColor =
      EVENT_COLORS[event.to] ||
      EVENT_COLORS[event.type] ||
      EVENT_COLORS["Planned"];
    // const childOperator = event.operator || event.machineName || 'Unassigned';

    const childOperatorFromNote = parseOperatorFromNote(event.note);
    const childOperator = childOperatorFromNote || operator;

    const childTask = {
      id: `order-${pwo_id}-event-${index}`,
      name: `${childStatusColor.label}${event.machineName ? ` • ${event.machineName}` : ""}`,
      type: "task",
      start: eventStart,
      end: eventEnd,
      project: `order-${pwo_id}`,
      progress: 100,
      dependencies: index > 0 ? [`order-${pwo_id}-event-${index - 1}`] : [],
      isDisabled: false,
      styles: {
        backgroundColor: childStatusColor.bar,
        // progressColor: childStatusColor.progress,
        // progressSelectedColor: childStatusColor.progress,
        progressColor: childStatusColor.bar,
        progressSelectedColor: childStatusColor.bar,

        backgroundSelectedColor: childStatusColor.bar,
      },
      cardData: parentTask?.cardData,
      operator: childOperator,
      metadata: {
        isChild: true,
        parentId: `order-${pwo_id}`,
        eventType: event.type,
        fromStatus: event.from,
        toStatus: event.to,
        machine: event.machineName,
        machineCode: event.machineCode,
        department: event.department,
        note: event.note,
        timestamp: event.timestamp,
        duration: formatDuration(eventStart, eventEnd),
        statusColor: childStatusColor,
        operator: childOperator,
      },
    };

    tasks.push(childTask);
  });

  return tasks;
};

const calculateOrderProgress = (events) => {
  if (!events || events.length === 0) return 0;

  const lastEvent = events[events.length - 1];
  const status = normalizeStatus(lastEvent.to || lastEvent.lane);

  const progressMap = {
    Completed: 100,
    InProduction: 60,
    QC: 80,
    Scheduled: 20,
    Planned: 0,
    OnHold: 30,
  };

  return progressMap[status] || 0;
};

// Task list

const TaskListHeaderDefault = ({ headerHeight, rowWidth, colors }) => {
  return (
    <div
      className="gantt-task-list-header"
      style={{
        height: headerHeight,
        width: rowWidth,
        display: "flex",
        alignItems: "center",
        background: colors.surface,
        borderBottom: `2px solid ${colors.border}`,
        position: "sticky",
        top: 0,
        zIndex: 20,
        fontWeight: 700,
        fontSize: 11,
        color: colors.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        padding: "0 12px",
      }}
    >
      <div style={{ width: 30 }}></div>
      <div style={{ flex: 1, minWidth: 0 }}>Name</div>
      <div style={{ width: 90, textAlign: "center" }}>From</div>
      <div style={{ width: 90, textAlign: "center" }}>To</div>
    </div>
  );
};

const TaskListTableDefault = ({
  rowHeight,
  rowWidth,
  tasks,
  onExpanderClick,
  colors,
  isDark,
}) => {
  return (
    <div style={{ width: rowWidth }}>
      {/* {tasks.map((task) => { */}
      {tasks.map((task, index) => {
        const avatarName =
          task.operator || task.cardData?.customer || "Unknown";
        const avatarColor = getAvatarColor(avatarName);
        const initials = getInitials(avatarName);

        // const rowBackground = index % 2 === 0 ? colors.background : colors.surface;

        const rowBackground =
          index % 2 === 0 ? colors.background : isDark ? "#1a2332" : "#f9fafb";

        return (
          <div
            key={task.id}
            style={{
              height: rowHeight,
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              borderBottom: `1px solid ${colors.borderLight}`,
              // background: colors.background,
              background: rowBackground,
              transition: "all 0.2s ease",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.surfaceHover;
            }}
            // onMouseLeave={(e) => {
            //   e.currentTarget.style.background = colors.background;
            // }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = rowBackground;
            }}
          >
            {/* EXPANDER + AVATAR (30px) */}
            <div
              style={{
                width: 30,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {task.metadata?.isParent && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpanderClick(task);
                  }}
                  style={{
                    fontSize: 10,
                    cursor: "pointer",
                    transform: task.hideChildren
                      ? "rotate(0deg)"
                      : "rotate(90deg)",
                    transition: "transform 0.2s ease",
                    display: "inline-block",
                    color: colors.textSecondary,
                  }}
                >
                  ▶
                </span>
              )}
            </div>

            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {/* Avatar Circle */}
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: avatarColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#FFFFFF",
                  flexShrink: 0,
                  boxShadow: `0 0 0 3px ${avatarColor}20`,
                  border: `2px solid ${colors.background}`,
                }}
              >
                {initials}
              </div>

              {/* Order Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    marginBottom: 2,
                    whiteSpace: "nowrap",
                    overflow: "visible",
                  }}
                >
                  {task.metadata?.orderNumber
                    ? `#${task.metadata.orderNumber}`
                    : task.name}
                </div>

                {/* Status Badge */}
                {task.metadata?.currentStatus && (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 6,
                      background:
                        task.metadata.statusColor?.bg || colors.surface,
                      color:
                        task.metadata.statusColor?.text || colors.textSecondary,
                    }}
                  >
                    {task.metadata.currentStatus}
                  </div>
                )}
              </div>
            </div>

            {/* FROM DATE COLUMN (90px) */}
            <div
              style={{
                width: 90,
                fontSize: 11,
                fontWeight: 600,
                color: colors.textSecondary,
                textAlign: "center",
                fontFamily: "ui-monospace, monospace",
              }}
            >
              {formatDateShort(task.start)}
            </div>

            {/* TO DATE COLUMN (90px) */}
            <div
              style={{
                width: 90,
                fontSize: 11,
                fontWeight: 600,
                color: colors.textSecondary,
                textAlign: "center",
                fontFamily: "ui-monospace, monospace",
              }}
            >
              {formatDateShort(task.end)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// TOOLTIP
// ============================================================================
const CustomTooltip = ({ task, colors }) => {
  const avatarName = task.operator || task.cardData?.customer || "Unknown";
  const avatarColor = getAvatarColor(avatarName);
  const initials = getInitials(avatarName);

  return (
    <div
      style={{
        background: colors.cardBg,
        border: `2px solid ${task.metadata?.statusColor?.bar || colors.border}`,
        borderRadius: 12,
        padding: 18,
        minWidth: 300,
        maxWidth: 380,
        boxShadow: colors.shadowLg,
        backdropFilter: "blur(12px)",
      }}
    >
      {task.metadata?.isParent ? (
        <>
          <div
            style={{
              fontWeight: 800,
              fontSize: 16,
              marginBottom: 14,
              color: colors.textPrimary,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: avatarColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: "#FFFFFF",
                flexShrink: 0,
                boxShadow: `0 4px 12px ${avatarColor}40`,
              }}
            >
              {initials}
            </div>
            <div style={{ flex: 1 }}>{task.name}</div>
          </div>

          <div
            style={{
              display: "grid",
              gap: 10,
              fontSize: 13,
              color: colors.textSecondary,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 600 }}>Status:</span>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "4px 10px",
                  borderRadius: 6,
                  background: task.metadata.statusColor?.bg,
                  color: task.metadata.statusColor?.text,
                }}
              >
                {task.metadata.currentStatus}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600 }}>Events:</span>
              <span style={{ fontWeight: 700, color: colors.textPrimary }}>
                {task.metadata.totalEvents} movements
              </span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600 }}>Progress:</span>
              <span style={{ fontWeight: 700, color: colors.info }}>
                {task.progress}%
              </span>
            </div>

            {task.operator && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Operator:</span>
                <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                  {task.operator}
                </span>
              </div>
            )}

            {task.cardData?.machine && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Machine:</span>
                <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                  {task.cardData.machine}
                </span>
              </div>
            )}

            {task.cardData?.machineCode && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Machine Code:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: colors.textSecondary,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 12,
                  }}
                >
                  {task.cardData.machineCode}
                </span>
              </div>
            )}

            <div
              style={{
                borderTop: `1px solid ${colors.borderLight}`,
                marginTop: 6,
                paddingTop: 10,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>From:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: colors.textPrimary,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 12,
                  }}
                >
                  {formatDateTime(task.start)}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>To:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: colors.textPrimary,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 12,
                  }}
                >
                  {formatDateTime(task.end)}
                </span>
              </div>
            </div>

            {task.cost && (
              <div
                style={{
                  borderTop: `1px solid ${colors.borderLight}`,
                  marginTop: 6,
                  paddingTop: 10,
                  display: "grid",
                  gap: 8,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ fontWeight: 600 }}>Revenue:</span>
                  <span style={{ fontWeight: 700, color: colors.success }}>
                    {formatCurrency(task.cost.totalRevenue)}
                  </span>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ fontWeight: 600 }}>Profit:</span>
                  <span
                    style={{
                      fontWeight: 700,
                      color:
                        task.cost.totalProfit > 0
                          ? colors.success
                          : colors.danger,
                    }}
                  >
                    {formatCurrency(task.cost.totalProfit)}
                  </span>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ fontWeight: 600 }}>Margin:</span>
                  <span style={{ fontWeight: 700, color: colors.info }}>
                    {formatPercent(task.cost.profitMargin)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              fontWeight: 800,
              fontSize: 15,
              marginBottom: 12,
              color: colors.textPrimary,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: avatarColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#FFFFFF",
                flexShrink: 0,
                boxShadow: `0 3px 10px ${avatarColor}40`,
              }}
            >
              {initials}
            </div>
            <div>
              {task.metadata?.fromStatus} → {task.metadata?.toStatus}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gap: 8,
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            {task.metadata?.machine && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Machine:</span>
                <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                  {task.metadata.machine}
                </span>
              </div>
            )}

            {task.metadata?.machineCode && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Machine Code:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: colors.textSecondary,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 11,
                  }}
                >
                  {task.metadata.machineCode}
                </span>
              </div>
            )}

            {task.metadata?.department && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Department:</span>
                <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                  {task.metadata.department}
                </span>
              </div>
            )}

            {task.operator && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Operator:</span>
                <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                  {task.operator}
                </span>
              </div>
            )}

            <div
              style={{
                borderTop: `1px solid ${colors.borderLight}`,
                marginTop: 4,
                paddingTop: 8,
                display: "grid",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>From:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: colors.textPrimary,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 11,
                  }}
                >
                  {formatDateTime(task.start)}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>To:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: colors.textPrimary,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 11,
                  }}
                >
                  {formatDateTime(task.end)}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>Duration:</span>
                <span style={{ fontWeight: 700, color: colors.info }}>
                  {task.metadata?.duration}
                </span>
              </div>
            </div>

            {task.metadata?.note && (
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: `${colors.danger}10`,
                  border: `1px solid ${colors.danger}30`,
                  borderRadius: 8,
                  fontSize: 11,
                  color: colors.danger,
                  fontWeight: 600,
                }}
              >
                ⚠️ {task.metadata.note}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// KPI CARD

const FinancialKPICard = memo(
  ({ icon: Icon, label, value, subtitle, colors }) => (
    <Card
      style={{
        background: colors.cardBg,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 12,
        height: "100%",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: colors.shadow,
      }}
      className="kpi-card-hover"
    >
      <Card.Body className="p-4">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: `${colors.info}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon style={{ fontSize: 24, color: colors.info }} />
          </div>
        </div>

        <div
          style={{
            fontSize: 11,
            color: colors.textMuted,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 8,
          }}
        >
          {label}
        </div>

        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            color: colors.textPrimary,
            lineHeight: 1,
            marginBottom: 6,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </div>

        {subtitle && (
          <div
            style={{
              fontSize: 13,
              color: colors.textSecondary,
              fontWeight: 600,
            }}
          >
            {subtitle}
          </div>
        )}
      </Card.Body>
    </Card>
  ),
);

const ThemeToggle = memo(() => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <Button
      variant="outline-secondary"
      onClick={toggleTheme}
      style={{
        padding: "10px 18px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        fontWeight: 700,
        borderRadius: 10,
        transition: "all 0.2s",
        border: "none",
        background: isDark ? "#334155" : "#F3F4F6",
        color: isDark ? "#F1F5F9" : "#111827",
      }}
    >
      {isDark ? (
        <LightModeIcon style={{ fontSize: 18 }} />
      ) : (
        <DarkModeIcon style={{ fontSize: 18 }} />
      )}
      {isDark ? "Light" : "Dark"}
    </Button>
  );
});

// MAIN COMPONENT

const ProductionGanttMovementInner = ({ department }) => {
  const { isDark } = useTheme();
  const colors = isDark ? THEMES.dark : THEMES.light;

  const [allTasks, setAllTasks] = useState([]);
  const [viewMode, setViewMode] = useState("Day");
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [ganttKey, setGanttKey] = useState(0);

  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [detailedCard, setDetailedCard] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    fetchProductionData();
  }, [department]);

  const fetchProductionData = async () => {
    setLoading(true);

    try {
      let apiUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps`;
      if (department && department !== "production") {
        apiUrl += `?department=${encodeURIComponent(department)}`;
      }

      const response = await axios.get(scopedProductionUrl(apiUrl), {
        headers: {
          accessToken: localStorage.getItem("accessToken"),
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        const ganttTasks = response.data.data.map((step, index) => {
          const startDate = step.plannedStart ? new Date(step.plannedStart) : null;
          const endDate = step.plannedFinish ? new Date(step.plannedFinish) : null;

          return {
            id: String(step.step_id || index),
            start: startDate,
            end: endDate,
            cardData: {
              id: step.external_key,
              stepid: step.step_id,
              orderNumber: step.projectNumber,
              projectName: step.projectName,
              productName: step.productName,
              customer: step.customerName,
              machine: step.machineName,
              machineCode: step.machineCode,
              qtyRequired: step.qtyRequired,
              qtyDone: step.qty_done,
              status: normalizeStatus(step.status),
              department: step.department,
              external_key: step.external_key,
              pwo_id: step.pwo_id,
              operator: step.operator || step.machineName,
              plannedStart: step.plannedStart,
              plannedFinish: step.plannedFinish,
              holdReason: step.holdReason,
              disabled: step.disabled,
            },
          };
        });

        await enrichWithAuditDataParallel(ganttTasks);
      }
    } catch (error) {
      console.error("Failed to fetch production steps:", error);
      toast.error("Failed to load production data");
    } finally {
      setLoading(false);
    }
  };

  const enrichWithAuditDataParallel = async (tasks) => {
    setAuditLoading(true);

    try {
      const enrichPromises = tasks.map(async (task) => {
        try {
          const [auditRes, costRes] = await Promise.all([
            axios
              .get(
                scopedProductionUrl(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/audit`),
                {
                  params: { external_key: task.cardData.external_key },
                  headers: { accessToken: localStorage.getItem("accessToken") },
                },
              )
              .catch(() => null),
            axios
              .get(
                scopedProductionUrl(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-details`),
                {
                  params: { external_key: task.cardData.external_key },
                  headers: { accessToken: localStorage.getItem("accessToken") },
                },
              )
              .catch(() => null),
          ]);

          let costData = null;
          if (costRes?.data?.success) {
            const { charges, meta } = costRes.data;
            const qty = task.cardData.qtyRequired || 1;
            const deptName =
              meta.eligible_departments?.[0]?.name || task.cardData.department;
            const deptCharges = charges.byDepartment?.[deptName] || [];
            const charge = deptCharges[0] || { net: 0, retail: 0 };

            const totalCost = charge.net * qty;
            const totalRevenue = charge.retail * qty;
            const totalProfit = totalRevenue - totalCost;
            const profitMargin =
              totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

            costData = { totalCost, totalRevenue, totalProfit, profitMargin };
          }

          if (auditRes?.data?.success && auditRes.data.events?.length > 0) {
            return transformAuditToGanttTasks(auditRes.data, task, costData);
          } else {
            const statusColor =
              EVENT_COLORS[task.cardData.status] || EVENT_COLORS["Planned"];
            return [
              {
                id: task.id,
                name: `#${task.cardData.orderNumber} - ${task.cardData.customer}`,
                type: "task",
                start: task.start,
                end: task.end,
                progress: 0,
                hideChildren: true, // Default collapsed for single tasks too
                isDisabled: false,
                styles: {
                  backgroundColor: statusColor.bar,
                  progressColor: statusColor.progress,
                },
                cardData: task.cardData,
                cost: costData,
                operator: task.cardData.operator || task.cardData.machine,
                metadata: {
                  statusColor: statusColor,
                  currentStatus: task.cardData.status,
                  operator: task.cardData.operator || task.cardData.machine,
                },
              },
            ];
          }
        } catch (error) {
          console.error(
            `Error processing task ${task.cardData.external_key}:`,
            error,
          );
          return [];
        }
      });

      const results = await Promise.all(enrichPromises);
      const enrichedTasks = results.flat();

      setAllTasks(enrichedTasks);
    } catch (error) {
      console.error("Failed to enrich with audit data:", error);
      toast.error("Failed to load movement history");
    } finally {
      setAuditLoading(false);
    }
  };

  const handleExpandAll = useCallback(() => {
    setAllTasks((prev) =>
      prev.map((task) =>
        task.metadata?.isParent ? { ...task, hideChildren: false } : task,
      ),
    );
    setGanttKey((prev) => prev + 1);
    toast.success("All orders expanded", { duration: 1500 });
  }, []);

  const handleCollapseAll = useCallback(() => {
    setAllTasks((prev) =>
      prev.map((task) =>
        task.metadata?.isParent ? { ...task, hideChildren: true } : task,
      ),
    );
    setGanttKey((prev) => prev + 1);
    toast.success("All orders collapsed", { duration: 1500 });
  }, []);

  const fetchCardDetails = async (externalKey) => {
    setModalLoading(true);
    try {
      const response = await axios.get(
        scopedProductionUrl(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-details?external_key=${externalKey}`),
        { headers: { accessToken: localStorage.getItem("accessToken") } },
      );
      if (response.data.success && response.data.data.length > 0) {
        setDetailedCard(response.data.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch card details:", error);
      toast.error("Failed to load order details");
    } finally {
      setModalLoading(false);
    }
  };

  const handleTaskClick = useCallback(
    (task) => {
      const fullTask = allTasks.find((t) => t.id === task.id);

      if (task.metadata?.isParent) {
        setAllTasks((prev) =>
          prev.map((t) =>
            t.id === task.id ? { ...t, hideChildren: !t.hideChildren } : t,
          ),
        );
        setGanttKey((prev) => prev + 1);

        if (fullTask?.cardData) {
          setSelectedCard(fullTask.cardData);
          setShowModal(true);
          fetchCardDetails(fullTask.cardData.external_key);
        }
      } else if (fullTask?.cardData) {
        setSelectedCard(fullTask.cardData);
        setShowModal(true);
        fetchCardDetails(fullTask.cardData.external_key);
      }
    },
    [allTasks],
  );

  const handleExpanderClick = useCallback((task) => {
    if (task.metadata?.isParent) {
      setAllTasks((prev) =>
        prev.map((t) =>
          t.id === task.id ? { ...t, hideChildren: !t.hideChildren } : t,
        ),
      );
      setGanttKey((prev) => prev + 1);
    }
  }, []);

  const visibleTasks = useMemo(() => {
    const parentMap = new Map();

    allTasks.forEach((task) => {
      if (task.metadata?.isParent) {
        parentMap.set(task.id, task.hideChildren);
      }
    });

    return allTasks.filter((task) => {
      if (task.metadata?.isParent) return true;

      if (task.metadata?.isChild) {
        const parentId = task.project || task.metadata.parentId;
        if (parentId) {
          const isCollapsed = parentMap.get(parentId);
          return !isCollapsed;
        }
      }

      return true;
    });
  }, [allTasks, ganttKey]);

  const financialStats = useMemo(() => {
    const parentTasks = allTasks.filter((t) => t.metadata?.isParent);
    const totalRevenue = parentTasks.reduce(
      (sum, t) => sum + (t.cost?.totalRevenue || 0),
      0,
    );
    const totalCost = parentTasks.reduce(
      (sum, t) => sum + (t.cost?.totalCost || 0),
      0,
    );
    const totalProfit = totalRevenue - totalCost;
    const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalCost,
      totalProfit,
      avgMargin,
      totalOrders: parentTasks.length,
    };
  }, [allTasks]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "600px",
          background: colors.background,
          borderRadius: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: 60, height: 60, marginBottom: 24 }}
          />
          <p
            style={{
              color: colors.textSecondary,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Loading order movements...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: colors.background,
        borderRadius: 16,
        border: `1px solid ${colors.borderLight}`,
        overflow: "hidden",
        boxShadow: colors.shadow,
      }}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2000,
          style: {
            fontFamily: "'Inter', sans-serif",
            background: isDark ? "#1E293B" : "#fff",
            color: isDark ? "#F1F5F9" : "#111827",
          },
        }}
      />

      {/* HEADER */}
      <div
        style={{
          background: colors.surface,
          borderBottom: `1px solid ${colors.borderLight}`,
          padding: "24px 32px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 800,
                color: colors.textPrimary,
                letterSpacing: "-0.02em",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <TimelineIcon style={{ fontSize: 28, color: colors.info }} />
              Order Movement Timeline
              <Badge
                bg="success"
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "6px 10px",
                  borderRadius: 6,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                v7.1 • DAY VIEW
              </Badge>
            </h4>
            <p
              style={{
                margin: "8px 0 0 0",
                color: colors.textSecondary,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Real-time tracking •{" "}
              {allTasks.filter((t) => t.metadata?.isParent).length} orders •{" "}
              {visibleTasks.length} visible tasks
            </p>
          </div>

          <div className="d-flex gap-2">
            <ButtonGroup>
              <Button
                variant="outline-secondary"
                onClick={handleExpandAll}
                disabled={auditLoading}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  padding: "10px 16px",
                  border: "none",
                  background: colors.surfaceHover,
                  color: colors.textPrimary,
                }}
              >
                <UnfoldMoreIcon style={{ fontSize: 16 }} />
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleCollapseAll}
                disabled={auditLoading}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  padding: "10px 16px",
                  border: "none",
                  background: colors.surfaceHover,
                  color: colors.textPrimary,
                }}
              >
                <UnfoldLessIcon style={{ fontSize: 16 }} />
              </Button>
            </ButtonGroup>

            <Button
              variant="outline-secondary"
              onClick={fetchProductionData}
              disabled={loading || auditLoading}
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: "10px 16px",
                border: "none",
                background: colors.surfaceHover,
                color: colors.textPrimary,
              }}
            >
              <RefreshIcon style={{ fontSize: 16 }} />
            </Button>

            <ThemeToggle />
          </div>
        </div>

       

        {/* View Mode Selector */}
        <div className="d-flex justify-content-between align-items-center">
          <ButtonGroup>
            {VIEW_MODES.map(({ mode, label, icon: Icon }) => (
              <Button
                key={mode}
                variant={viewMode === mode ? "primary" : "outline-secondary"}
                onClick={() => setViewMode(mode)}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  padding: "10px 20px",
                  border: "none",
                  background:
                    viewMode === mode ? colors.info : colors.surfaceHover,
                  color: viewMode === mode ? "#FFFFFF" : colors.textPrimary,
                }}
              >
                <Icon style={{ fontSize: 16, marginRight: 6 }} />
                {label}
              </Button>
            ))}
          </ButtonGroup>

          {auditLoading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: colors.textSecondary,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Spinner animation="border" size="sm" />
              Loading movement history...
            </div>
          )}
        </div>
      </div>

      {/* GANTT CHART - DARK MODE */}
      <div
        id="gantt-movement-container"
        style={{
          background: colors.background, //  DARK MODE BACKGROUND
          padding: 24,
          minHeight: 500,
        }}
      >
        {allTasks.length > 0 ? (
          <div className="gantt-wrapper" key={ganttKey}>
            <ProductionFrappeGantt
              rows={visibleTasks}
              viewMode={viewMode}
              isDark={isDark}
              onTaskSelect={handleTaskClick}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 100 }}>
            <ScheduleIcon
              style={{
                fontSize: 64,
                color: colors.textMuted,
                marginBottom: 20,
                opacity: 0.5,
              }}
            />
            <p
              style={{
                color: colors.textSecondary,
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              No order movements to display
            </p>
          </div>
        )}
      </div>

      {/* LEGEND */}
      <div
        style={{
          background: colors.surface,
          borderTop: `1px solid ${colors.borderLight}`,
          padding: "20px 32px",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: colors.textMuted,
            marginBottom: 16,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Status Legend
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {Object.entries(EVENT_COLORS)
            .filter(([key]) =>
              [
                "Planned",
                "Scheduled",
                "InProduction",
                "QC",
                "Completed",
                "OnHold",
              ].includes(key),
            )
            .map(([key, value]) => (
              <div
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 14px",
                  background: value.bg,
                  borderRadius: 8,
                  border: `1px solid ${colors.borderLight}`,
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = colors.shadow;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 16,
                    borderRadius: 4,
                    background: value.bar,
                  }}
                />
                <span
                  style={{ fontSize: 12, color: value.text, fontWeight: 700 }}
                >
                  {value.label}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* MODAL */}
      {showModal && selectedCard && (
        <JobCardModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setDetailedCard(null);
          }}
          card={selectedCard}
          detailedCard={detailedCard}
          modalLoading={modalLoading}
          onPatchCard={fetchProductionData}
          orderMeta={detailedCard?.meta}
          orderCosting={detailedCard?.costing}
          orderCharges={detailedCard?.charges}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
        }
        
        .kpi-card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .kpi-card-hover:hover {
          transform: translateY(-4px);
          box-shadow: ${colors.shadowMd} !important;
        }
        
        .gantt-wrapper {
          animation: fadeIn 0.4s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* DARK MODE GANTT STYLING */
        .gantt_task_line,
        .gantt_side_content,
        .gantt_task_content,
        .gantt-vertical-container,
        .gantt-horizontal-container,
        ._1yf8T {
          background: ${colors.background} !important;
        }
        
        .gantt-task-list-header-cell,
        .gantt-task-list-cell {
          background: ${colors.surface} !important;
          color: ${colors.textPrimary} !important;
          border-color: ${colors.borderLight} !important;
          font-weight: 600 !important;
        }
        
        .gantt-task-list-row {
          border-color: ${colors.borderLight} !important;
        }
        
        /* GANTT GRID DARK MODE */
        #gantt-movement-container ._3lLk3,
        #gantt-movement-container ._2xXRz,
        #gantt-movement-container ._3KrqV,
        #gantt-movement-container .gantt_grid_scale,
        #gantt-movement-container .gantt_grid_data {
          background: ${colors.background} !important;
          color: ${colors.textPrimary} !important;
        }
        
        #gantt-movement-container ._3yz6g {
          stroke: ${colors.borderLight} !important;
        }
        
        /* WEEK/MONTH HEADERS DARK MODE */
        ._2E4e2,
        .gantt_task,
        .gantt-calendar-header {
          background: ${colors.surface} !important;
          color: ${colors.textPrimary} !important;
        }

/* SVG background */

/* Header calendar background */
#gantt-movement-container rect._35nLX {
  fill: ${colors.surface} !important;
}

/* Grid row backgrounds */
#gantt-movement-container rect._2dZTy {
  fill: ${colors.background} !important;
}

/* Row border lines */
#gantt-movement-container line._3rUKi {
  stroke: ${colors.borderLight} !important;
}

/* Grid tick lines (vertical) */
#gantt-movement-container line._RuwuK {
  stroke: ${colors.border} !important;
  // stroke-opacity: 0.3 !important;
  stroke-opacity: 1 !important;  /* Change 0.3 to 1 */
}

/* Calendar date text */
#gantt-movement-container text._9w8d5 {
  fill: ${colors.textPrimary} !important;
}

/* Month/year text */
#gantt-movement-container text._2q1Kt {
  fill: ${colors.textPrimary} !important;
}

/* Task label text */
#gantt-movement-container text._3KcaM {
  fill: ${colors.textPrimary} !important;
}

/* Grid containers */
#gantt-movement-container ._1CS8g,
#gantt-movement-container ._2Jqfu,
#gantt-movement-container ._3BkQG,
#gantt-movement-container ._1yf8T {
  background: ${colors.background} !important;
}


/* LEFT SIDE TASK LIST - ALTERNATING ROWS & BORDERS */

/* Add alternating row colors */
#gantt-movement-container .gantt-task-list-row:nth-child(odd) {
  background: ${colors.background} !important;
}

#gantt-movement-container .gantt-task-list-row:nth-child(even) {
  background: ${isDark ? "#1a2332" : "#f9fafb"} !important;
}

/* Make row borders visible */
#gantt-movement-container .gantt-task-list-row {
  border-bottom: 1px solid ${colors.borderLight} !important;
}

/* Header border */
#gantt-movement-container .gantt-task-list-header {
  border-bottom: 2px solid ${colors.border} !important;
}





        
        /* BAR STYLING */
        .gantt-task-bar {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          cursor: pointer !important;
          border-radius: 8px !important;
        }
          /* OPACITY */
._2RbVy {
  opacity: 1 !important; 
}


.gantt-task-bar,
.gantt-bar,
._3zRJQ {
  opacity: 1 !important; 
}
        
        .gantt-task-bar:hover {
          transform: translateY(-2px) !important;
          filter: brightness(1.1) !important;
          box-shadow: ${colors.shadowMd} !important;
        }
        
        .gantt-task-bar-content {
          font-weight: 600 !important;
          font-size: 12px !important;
        }
        
        .gantt-arrow {
          stroke-width: 2px !important;
          opacity: 0.6 !important;
          transition: all 0.2s ease !important;
        }
        
        .gantt-arrow:hover {
          stroke-width: 3px !important;
          opacity: 1 !important;
        }
        
        /* SCROLLBAR STYLING */
        ::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: ${colors.surface};
          border-radius: 8px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: ${colors.border};
          borderRadius: 8px;
          transition: background 0.2s;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.textMuted};
        }
        
        /* BUTTON GROUP STYLING */
        .btn-group .btn {
          transition: all 0.2s ease;
        }
        
        .btn-group .btn:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

const ProductionGanttMovement = (props) => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider>
      <ProductionGanttMovementInner {...props} />
    </ThemeProvider>
  </StyledEngineProvider>
);

export default ProductionGanttMovement;
